/**
 * 一覧表の並べ替え・絞り込みの共通部品(⑦指示: すべてのページにフィルター・ソート)。
 *  - useSort / sortRows: 見出しクリックで昇順→降順→解除
 *  - SortTh: 並べ替えできる見出しセル(説明の吹き出し HelpTip も載せられる)
 *  - SearchBox / ChipSelect: キーワード検索とチップ式の絞り込み
 */
import { useCallback, useState, type CSSProperties, type ReactNode } from 'react';
import HelpTip, { type Help } from './HelpTip';
import { IconSort, IconSortAsc, IconSortDesc } from './icons';

export type SortDir = 'asc' | 'desc';
export type SortState<K extends string = string> = { key: K; dir: SortDir } | null;
export type SortValue = string | number | null | undefined;

export function useSort<K extends string>(initial: SortState<K> = null) {
  const [sort, setSort] = useState<SortState<K>>(initial);
  /** 同じ列を押すたびに 既定の向き → 逆向き → 解除 */
  const toggle = useCallback((key: K, defaultDir: SortDir = 'desc') => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: defaultDir };
      if (prev.dir === defaultDir) return { key, dir: defaultDir === 'asc' ? 'desc' : 'asc' };
      return null;
    });
  }, []);
  return { sort, toggle, setSort };
}

function compare(a: SortValue, b: SortValue): number {
  const an = a === null || a === undefined || (typeof a === 'number' && !Number.isFinite(a));
  const bn = b === null || b === undefined || (typeof b === 'number' && !Number.isFinite(b));
  if (an && bn) return 0;
  if (an) return 1; // 空は向きに関係なく最後
  if (bn) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), 'ja');
}

/** sort が null なら元の順のまま */
export function sortRows<T, K extends string>(rows: T[], sort: SortState<K>, pick: (row: T, key: K) => SortValue): T[] {
  if (!sort) return rows;
  const dir = sort.dir === 'asc' ? 1 : -1;
  return [...rows]
    .map((row, i) => ({ row, i }))
    .sort((x, y) => {
      const va = pick(x.row, sort.key);
      const vb = pick(y.row, sort.key);
      const an = va === null || va === undefined;
      const bn = vb === null || vb === undefined;
      if (an || bn) return compare(va, vb) || x.i - y.i;
      return compare(va, vb) * dir || x.i - y.i;
    })
    .map((x) => x.row);
}

export function SortTh<K extends string>({
  label,
  sortKey,
  sort,
  onSort,
  defaultDir = 'desc',
  help,
  className,
  style,
  colSpan,
  rowSpan,
}: {
  label: ReactNode;
  sortKey?: K;
  sort?: SortState<K>;
  onSort?: (key: K, defaultDir?: SortDir) => void;
  defaultDir?: SortDir;
  help?: Help;
  className?: string;
  style?: CSSProperties;
  colSpan?: number;
  rowSpan?: number;
}) {
  const active = !!sort && !!sortKey && sort.key === sortKey;
  const cls = [className ?? '', sortKey ? 'th-sort' : '', active ? 'on' : ''].filter(Boolean).join(' ');
  return (
    <th className={cls || undefined} style={style} colSpan={colSpan} rowSpan={rowSpan} onClick={sortKey && onSort ? () => onSort(sortKey, defaultDir) : undefined}>
      {help ? <HelpTip help={help}>{label}</HelpTip> : label}
      {sortKey && <span className="sort-ind">{active ? (sort!.dir === 'asc' ? <IconSortAsc size={11} /> : <IconSortDesc size={11} />) : <IconSort size={11} />}</span>}
    </th>
  );
}

export function SearchBox({ value, onChange, placeholder, style, className }: { value: string; onChange: (v: string) => void; placeholder?: string; style?: CSSProperties; className?: string }) {
  return (
    <input
      type="search"
      className={className}
      value={value}
      placeholder={placeholder ?? '検索'}
      aria-label={placeholder ?? '検索'}
      onChange={(e) => onChange(e.target.value)}
      // className を渡したら見た目は CSS で決める(共通UIの FilterBar では ui-search)。渡さない画面は従来の見た目
      style={className ? style : { height: 30, padding: '0 10px', border: '1px solid var(--line)', borderRadius: 6, fontSize: 12.5, fontFamily: 'inherit', minWidth: 160, ...style }}
    />
  );
}

/** チップ式の単一選択(先頭に「すべて」) */
export function ChipSelect<K extends string>({
  options,
  value,
  onChange,
  allLabel = 'すべて',
  label,
}: {
  options: { key: K; label: string }[];
  value: K | 'all';
  onChange: (v: K | 'all') => void;
  allLabel?: string | null;
  label?: string;
}) {
  return (
    <div className="chips" style={{ marginBottom: 0, alignItems: 'center' }}>
      {label && <span className="hint" style={{ marginRight: 2 }}>{label}</span>}
      {allLabel !== null && (
        <button type="button" className={`chip${value === 'all' ? ' on' : ''}`} onClick={() => onChange('all')}>
          {allLabel}
        </button>
      )}
      {options.map((o) => (
        <button key={o.key} type="button" className={`chip${value === o.key ? ' on' : ''}`} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** キーワードが空なら常に一致。全角スペース区切りのAND検索 */
export function matchKeyword(keyword: string, ...fields: (string | null | undefined)[]): boolean {
  const terms = keyword
    .toLowerCase()
    .split(/[\s　]+/)
    .filter(Boolean);
  if (terms.length === 0) return true;
  const hay = fields.map((f) => (f ?? '').toLowerCase()).join(' ');
  return terms.every((t) => hay.includes(t));
}
